import { useEffect, useState } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Button, Snackbar, Alert, Typography, Divider,
} from "@mui/material";
import { adminApi } from "rsa/adminApi";
import SectionHeader from "../Components/SectionHeader";

const cardSx = { borderRadius: "15px", boxShadow: "rgba(0,0,0,0.1) 0px 10px 15px -3px, rgba(0,0,0,0.05) 0px 4px 6px -2px", p: 2 };

export default function FeesPage() {
  const [types, setTypes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  // Session-only change log — no audit table exists server-side for this yet
  // (see tasks/06-backend-gap-work.md), so this resets on reload rather than
  // silently pretending to be a persisted history.
  const [history, setHistory] = useState([]);

  const load = async () => {
    try { setError(""); setTypes(await adminApi("GET", "/rsa/service-types")); }
    catch (e) { setError(e.status === 401 ? "Session expired — sign in again." : e.message); }
  };
  useEffect(() => { load(); }, []);

  const isDirty = (t) => {
    const d = drafts[t.id];
    return d && (+d.baseFee !== +t.base_fee || +d.perKmFee !== +t.per_km_fee);
  };

  const save = async (t) => {
    const d = drafts[t.id];
    const updated = await adminApi("PATCH", `/admin/rsa/service-types/${t.id}`, {
      baseFee: +d.baseFee, perKmFee: +d.perKmFee,
    });
    setTypes((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
    setHistory((h) => [{ name: t.name, at: new Date().toLocaleTimeString(), base: updated.base_fee, perKm: updated.per_km_fee }, ...h]);
    setSaved(true);
  };

  return (
    <Box>
      <SectionHeader title="Fee Model" />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box display="flex" gap={2} flexWrap="wrap">
        <Card sx={{ ...cardSx, flex: "2 1 420px" }}>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Service</TableCell><TableCell>Base ₹</TableCell><TableCell>₹/km</TableCell><TableCell /></TableRow>
            </TableHead>
            <TableBody>
              {types.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    <TextField size="small" type="number" defaultValue={Math.round(t.base_fee)}
                      onChange={(e) => setDrafts((p) => ({ ...p, [t.id]: { ...p[t.id], baseFee: e.target.value, perKmFee: p[t.id]?.perKmFee ?? t.per_km_fee } }))} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" defaultValue={Math.round(t.per_km_fee)}
                      onChange={(e) => setDrafts((p) => ({ ...p, [t.id]: { ...p[t.id], perKmFee: e.target.value, baseFee: p[t.id]?.baseFee ?? t.base_fee } }))} />
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="contained" disabled={!isDirty(t)} onClick={() => save(t)}>Save</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card sx={{ ...cardSx, flex: "1 1 260px" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Change history (this session)</Typography>
          <Divider sx={{ mb: 1 }} />
          {history.length === 0 && <Typography variant="body2" color="text.secondary">No edits yet.</Typography>}
          {history.map((h, i) => (
            <Box key={i} mb={1}>
              <Typography variant="body2" fontWeight={600}>{h.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {h.at} · base ₹{Math.round(h.base)} · ₹{Math.round(h.perKm)}/km
              </Typography>
            </Box>
          ))}
        </Card>
      </Box>

      <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success" onClose={() => setSaved(false)}>Fee saved.</Alert>
      </Snackbar>
    </Box>
  );
}
